import { FirebaseError } from 'firebase/app';
import { ConfirmationResult, signInWithPhoneNumber } from 'firebase/auth';
import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { AUTH } from '../../auth/FirebaseContext';
import { SignInMethod, useLoginFlow } from '../../auth/LoginProvider';
import { isCustomDomainMode } from '../../utils/customDomainUtils';
import { Alert } from '../ui/Alert';

type SelectVerificationMethodProps = {
  next(confirm?: ConfirmationResult): void;
  back(): void;
};

const SelectVerificationMethod = ({ next, back }: SelectVerificationMethodProps) => {
  const { phoneToVerify, recaptcha, signInMethod } = useLoginFlow();
  const [error, setError] = useState<string | null>(null);
  const codeSent = useRef<boolean>(false);

  // this one-time effect doesn't work wrt recaptcha, so we check that as a dependency, and use a REF to gate
  useEffect(() => {
    if (codeSent.current) {
      return;
    }
    const SendOnce = async () => {
      if (signInMethod === SignInMethod.Email) {
        codeSent.current = true;
        // Email verification is handled by the email-sent confirmation screen
        next();
        return;
      }
      if (signInMethod === SignInMethod.Phone && recaptcha) {
        codeSent.current = true;
        await signInWithPhoneNumber(AUTH, phoneToVerify, recaptcha)
          .then(async (nextPayload) => {
            next(nextPayload);
          })
          .catch((e) => {
            console.error('signInWithPhoneNumber failed:', {
              code: e.code,
              message: e.message,
              phoneNumber: phoneToVerify,
              isCustomDomain: isCustomDomainMode(),
              error: e
            });

            if (e instanceof FirebaseError && e.code === 'auth/too-many-requests') {
              setError(
                'Too many attempts. Please try again later. Custom domains must be whitelisted in Firebase.'
              );
              return;
            }
            // Check for phone authentication failures on custom domains
            if (
              isCustomDomainMode() &&
              (e.code === 'auth/unauthorized-domain' ||
                (e.message?.includes('domain') && e.message?.includes('authorized')))
            ) {
              setError(
                'Phone authentication failed. Custom domains must be whitelisted in Firebase.'
              );
              return;
            }

            // Show the actual Firebase error for debugging
            const errorMessage = e.code
              ? `Firebase Error (${e.code}): ${e.message}`
              : e.message || 'Unknown error';
            setError(`Phone auth failed: ${errorMessage}`);
          });
        return;
      }
    };

    SendOnce();
  }, [recaptcha, next, phoneToVerify, signInMethod]);

  return (
    <div className="flex flex-col justify-between min-h-[260px] space-y-4">
      {!error && (
        <>
          <p className="text-sm">Sending login code...</p>
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary dark:text-secondary" />
          </div>
        </>
      )}
      {error && <Alert variant="warning">{error}</Alert>}
      <button
        type="button"
        onClick={back}
        className="w-fit text-primary dark:text-secondary hover:underline cursor-pointer text-sm"
      >
        Back
      </button>
    </div>
  );
};

export default SelectVerificationMethod;
